import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, GeoJSON, Tooltip } from 'react-leaflet';
import FixMapResize from './FixMapResize';
import { Line } from 'react-chartjs-2';
import Draggable from 'react-draggable';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  ChartTooltip,
  Legend
);

export default function IndicatorMap({ year, indicator }) {
  const [geoData, setGeoData] = useState(null);
  const [timeSeries, setTimeSeries] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);

  const formatValue = (val, indicator) => {
    if (val == null) return 'N/A';

    const rounded = val.toFixed(2);

    switch (indicator) {
      case 'unemployment':
        return `${rounded}%`;
      case 'precipitation':
        return `${rounded} mm`;
      case 'temperature':
        return `${rounded} °C`;
      case 'population':
        return `${Number(val).toLocaleString()}`; // thousands separator
      default:
        return rounded;
    }
  };

  const indicatorLabel = (indicator) => {
    switch (indicator) {
      case 'unemployment':
        return 'Unemployment Rate';
      case 'precipitation':
        return 'Annual Precipitation';
      case 'temperature':
        return 'Average Temperature';
      case 'population':
        return 'Population';
      default:
        return indicator;
    }
  };

  useEffect(() => {
    const fetchGeoJSON = async () => {
      try {
        console.log(`Fetching GeoJSON for year ${year}`);
        const res = await fetch(
          `${process.env.REACT_APP_GEOJSON_API_URL}?year=${year}`
        );

        if (!res.ok) throw new Error(`Failed to fetch GeoJSON for year ${year}`);

        const text = await res.text();
        const parsed = JSON.parse(text);
        setGeoData(parsed);
      } catch (error) {
        console.error(error);
        setGeoData(null);
      }
    };

    fetchGeoJSON();
  }, [year]);

  const fetchTimeSeries = async (country) => {
    try {
      const url = `${process.env.REACT_APP_INDICATOR_API_URL}?indicator=${indicator}&country=${encodeURIComponent(country)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to fetch time series for ${country}`);
      const data = await res.json();
      setTimeSeries({ [data.country]: data.time_series });
      setSelectedCountry(country);
      // console.log(`Fetched time series for ${country}`, data); /* debug print */
    } catch (err) {
      console.error(err);
      setTimeSeries(null);
      setSelectedCountry(null);
    }
  };

  const getStyle = feature => {
    const value = feature.properties[indicator];
    if (value == null) {
      return {
        color: '#555',
        weight: 0.5,
        fillOpacity: 0.7,
        fillColor: '#ccc'
      };
    }

    let fillColor = '#ccc';

    if (indicator === 'unemployment') {
      fillColor =
        value > 20 ? '#800026' :
        value > 15 ? '#BD0026' :
        value > 10 ? '#E31A1C' :
        value > 5  ? '#FC4E2A' :
        value > 2  ? '#FD8D3C' :
        value > 0  ? '#FEB24C' :
                     '#FFEDA0';
    } else if (indicator === 'precipitation') {
      fillColor =
        value > 2000 ? '#08306b' :
        value > 1500 ? '#2171b5' :
        value > 1000 ? '#4292c6' :
        value > 750  ? '#6baed6' :
        value > 500  ? '#9ecae1' :
        value > 250  ? '#c6dbef' :
                       '#deebf7';
    } else if (indicator === 'population') {
      const logVal = Math.log10(value + 1);
      fillColor =
        logVal > 8 ? '#00441b' :
        logVal > 7 ? '#006d2c' :
        logVal > 6 ? '#238b45' :
        logVal > 5 ? '#41ab5d' :
        logVal > 4 ? '#74c476' :
        logVal > 3 ? '#a1d99b' :
                     '#c7e9c0';
    } else if (indicator === 'temperature') {
      fillColor =
        value > 30 ? '#7f0000' :
        value > 27 ? '#b30000' :
        value > 24 ? '#e34a33' :
        value > 21 ? '#fc8d59' :
        value > 18 ? '#fdbb84' :
        value > 15 ? '#fdd49e' :
                     '#fef0d9';
    }

    return {
      color: '#555',
      weight: 0.5,
      fillOpacity: 0.7,
      fillColor
    };
  };

  const onEachFeature = (feature, layer) => {
    const { ADMIN } = feature.properties;
    const value = feature.properties[indicator];

    let hoverTimeout;

    layer.on('mouseover', () => {
      hoverTimeout = setTimeout(() => {
        layer.bindTooltip(
          `<strong>${ADMIN}</strong><br/>${indicatorLabel(indicator)}: ${formatValue(value, indicator)}`,
          { sticky: true }
        ).openTooltip();
      }, 750); // 750 milliseconds
    });

    layer.on('mouseout', () => {
      clearTimeout(hoverTimeout);
      layer.closeTooltip();
    });

    layer.on('click', () => {
      fetchTimeSeries(ADMIN);
      layer.bringToBack(); // removes focus styling
    });
  };

  return (
    <div className="map-wrapper">
      <MapContainer
        center={[10, 0]}
        zoom={2}
        style={{
          height: '500px',
          width: '70%',
          display: 'block',
          marginTop: '1.5rem',
          marginLeft: 'auto',
          marginRight: 'auto',
          zIndex: 0
        }}
      >
        <FixMapResize />
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />
        {geoData && (
          <GeoJSON
            key={`${indicator}-${year}`}
            data={geoData}
            style={getStyle}
            onEachFeature={onEachFeature}
          />
        )}
      </MapContainer>

      {selectedCountry && timeSeries && timeSeries[selectedCountry] ? (
        <>
        <Draggable
          handle=".drag-handle"
          cancel=".popup-close-button"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '120px',
              right: '50px',
              width: '360px',
              maxHeight: '500px',
              overflowY: 'auto',
              background: 'white',
              border: '1px solid #999',
              borderRadius: '8px',
              padding: '1rem',
              boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
              zIndex: 9999,
              pointerEvents: 'auto' // explicitly allow interaction
            }}
          >
          <button
            className="popup-close-button"
            onClick={() => {
              setSelectedCountry(null);
              setTimeSeries(null);
            }}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              padding: '0.3rem 0.5rem',
              fontSize: '0.8rem',
              cursor: 'pointer',
              zIndex: 10000,                  // Ensure it's above map
              background: '#f0f0f0',
              border: '1px solid #aaa',
              borderRadius: '4px',
              touchAction: 'manipulation'     // Improves mobile tap behavior
            }}
          >
            &times;
          </button>
            <h3 style={{ marginTop: 0 }}>{selectedCountry}</h3>
            <Line
              data={{
                labels: Object.keys(timeSeries[selectedCountry]),
                datasets: [
                  {
                    label: indicatorLabel(indicator),
                    data: Object.values(timeSeries[selectedCountry]),
                    borderColor: '#3e95cd',
                    fill: false,
                    tension: 0.3,
                    pointRadius: 2
                  }
                ]
              }}
              options={{
                responsive: true,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: context => {
                        const val = context.raw;
                        return formatValue(val, indicator);
                      }
                    }
                  },
                  title: {
                    display: true,
                    text: indicatorLabel(indicator),
                    font: { size: 14 }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: false,
                    ticks: {
                      callback: val => formatValue(val, indicator)
                    }
                  }
                }
              }}
            />
          </div>
        </Draggable>
        </>
      ) : null}
    </div>
  );
}
